import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	INLINE_LUMINA_STATUS_PREFIX,
	isInlineLuminaStatus,
} from '@atlassian/conversation-assistant-content-transformer/utils/inline-lumina';

import { InlineCitation } from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/inline-citation/inline-citation';
import { StatusCitation } from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/status-citation';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { LUMINA_CONTENT_PREFIX } from '../../../ai-answer-dialog/utils/fetchAIAnswer';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { useLuminaSourceClickAnalytics } from './hooks/use-lumina-source-click-analytics';
const LUMINA_START_TAG = '<lumina>';
const LUMINA_END_TAG = '</lumina>';

export type StatusComponentProps = {
	localId: string;
	text: string;
	color?: string;
};

const LuminaLayout = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian-internal_lumina-layout" */
		'@atlassian/lumina'
	).then((mod) => ({ default: mod.LuminaLayout })),
);

const InlineLuminaLayout = ({
	luminaContent,
	cloudId,
	orgId,
	requestingUserId,
	sourceProduct,
}: {
	cloudId: string;
	luminaContent: string;
	orgId: string;
	requestingUserId: string;
	sourceProduct: string;
}): React.JSX.Element => {
	const aiAnswerDialogContext = useAIAnswerContext();
	const onSourceClick = useLuminaSourceClickAnalytics({ aiAnswerDialogContext });
	const { sessionId } = aiAnswerDialogContext.commonAttributes;

	return (
		<LuminaLayout
			luminaTagResponse={`${LUMINA_START_TAG}${luminaContent}${LUMINA_END_TAG}`}
			source="luminaSAIN"
			smartLinkRenderer="embed"
			cloudId={cloudId}
			orgId={orgId}
			requestingUserId={requestingUserId}
			sourceProduct={sourceProduct}
			sessionId={sessionId}
			onSourceClick={onSourceClick}
		/>
	);
};

export const renderStatusComponent = (
	props: StatusComponentProps,
	markdownData: Record<string, string> | undefined,
	appContext: { orgId: string; cloudId: string; sourceProduct: string },
	userId: string,
): React.JSX.Element => {
	if (isInlineLuminaStatus(props.localId)) {
		// retrieve idx of status node created by extension
		const luminaIdx = parseInt(props.text.replace(INLINE_LUMINA_STATUS_PREFIX, ''), 10);
		// map idx to content from markdownData created during streaming
		const luminaContent = markdownData?.[`${LUMINA_CONTENT_PREFIX}${luminaIdx}`] ?? '';

		if (luminaContent) {
			return (
				<InlineLuminaLayout
					luminaContent={luminaContent}
					cloudId={appContext.cloudId}
					orgId={appContext.orgId}
					requestingUserId={userId}
					sourceProduct={appContext.sourceProduct}
				/>
			);
		}
	}

	if (
		fg('kd_sain_reduce_inline_citations') ||
		SAINFeatureControl.isInlineCitationsUpdateEnabled()
	) {
		return <InlineCitation {...props} />;
	}

	return <StatusCitation {...props} />;
};
